import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";
import { getAppTimeZone, formatDate } from "@/lib/dates";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Link2,
  RefreshCcw,
  Save,
  MessageCircle,
  Database,
  CheckCircle2,
  AlertCircle,
  Pencil,
} from "lucide-react";

import AccountLookupModal from "./AccountLookupModal";




import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";   // 👈 NEW

/* ---------------- Helpers ---------------- */
// Build YYYY-MM-DD in *local* time (avoid UTC day-shift)
const fmtIsoLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const startOfWeekMon = (d: Date) => {
  const x = new Date(d);
  const dow = x.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfWeekMonToSun = (d: Date) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};
const addDays = (d: Date, days: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

// Weekday name (e.g. "Monday"/"Mon"), rendered in the app's configured timezone.
const weekdayName = (d: Date, style: "long" | "short" = "long") =>
  new Intl.DateTimeFormat("en-NZ", { weekday: style, timeZone: getAppTimeZone() }).format(d);
const fmtHHMM = (t?: string | null) => {
  if (!t) return "-";
  const s = t.length === 5 ? t : t.slice(0, 5);
  return s || "-";
};
const last3 = (s?: string | null) =>
  s && s.length >= 3 ? s.slice(-3) : (s || "-");
const last4 = (s?: string | null) =>
  s && s.length >= 4 ? s.slice(-4) : (s || "-");
const firstBeforeComma = (s?: string | null) => {
  if (!s) return "-";
  const i = s.indexOf(",");
  return i === -1 ? s : s.slice(0, i);
};
const fmtNum = (v: any) => (isNaN(Number(v)) ? "0.00" : Number(v).toFixed(2));
const sum = (xs: number[]) => xs.reduce((a, b) => a + (Number(b) || 0), 0);

// --- UI helper for colored boolean pills ---
const pillClass = (on: boolean, enabled: boolean) =>
  enabled
    ? (on ? "bg-green-700 text-white" : "bg-red-700 text-white")
    : "bg-neutral-800 text-neutral-300 border border-border";

/**
 * A compact boolean dropdown (✓/✗) with red/green/grey pill styling.
 */
export function BoolPillSelect({
  value,
  onChange,
  enabled = true,
  width = "w-9",          // "w-9" or "w-12" etc
  height = "h-6",
  className = "",
  trueLabel = "✓ True",
  falseLabel = "✗ False",
  centerText = true,
}: {
  value: boolean | null | undefined;            // current value
  onChange: (next: boolean) => void;            // called with true/false
  enabled?: boolean;                            // whether to color (else grey)
  width?: string;
  height?: string;
  className?: string;
  trueLabel?: string;
  falseLabel?: string;
  centerText?: boolean;
}) {
  const val = value ? "true" : "false";
  return (
    <Select value={val} onValueChange={(v) => onChange(v === "true")}>
      <SelectTrigger
        className={[
          width, height, "text-[11px] font-semibold rounded transition px-0.5",
          centerText ? "text-center" : "",
          pillClass(Boolean(value), enabled),
          className,
        ].join(" ")}
      >
        <SelectValue>{value ? "✓" : "✗"}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="true">{trueLabel}</SelectItem>
        <SelectItem value="false">{falseLabel}</SelectItem>
      </SelectContent>
    </Select>
  );
}

// Treat any positive number as “has value”
const hasValue = (n: any) => Number(n || 0) > 0;

// ---- Select helpers ----
const UNSET = "__unset__";
const toSelectVal = (v?: string | null) => (v && v.trim() ? v : UNSET);
const fromSelectVal = (v: string) => (v === UNSET ? null : v);

/* ---------------- Types ---------------- */
type MetricRow = {
  label: "EFTPOS" | "eCard" | "TM" | "TMHoist";
  validated: number;
  linked: number;
  nulls: number;
};

type DcpRow = {
  id: string;
  taxicaller_job_id: string;
  driver_name: string;
  pu_date: string | null;
  pu_time: string | null;
  vehicle_number: string | null;
  eft_id: string | null;
  Account_ID?: string | null;
  passenger?: string | null;
  pickup?: string | null;

  cash_amt: number;
  eftpos_amt: number;
  totalmobility_amt: number;
  tm_hoist_amt: number;
  account_amt: number;
  other_amt: number;
  discount: number;
  ecard_amt: number;

  eftpos_valid: boolean;
  total_mobility_valid: boolean;
  tm_hoist_valid: boolean;
  ecard_valid: boolean;
  reconciled: boolean;
  driver_notes?: string | null;   // 👈 NEW
};

type RwRow = {
  id: number;
  driver_number: string | null;
  vehicle_identifier: string | null;
  trip_date: string | null;
  start_time: string | null;
  fare_total: number | null;
  concession_amount: number | null;
  client_first_name: string | null;
  pickup_location: string | null;
  taxicaller_job_id: string | null;
  dcp_tmm_matched: boolean;
  dcp_tmh_matched: boolean;
  operator_transaction_ref: string | null;
  hoist_deployed: boolean | null;
  hoist_true_count: number | null;
  start_ts: string | null;
};

type EftRow = {
  id: number;
  terminal_id: string;
  transaction_date: string | null;
  settlement_date: string | null;
  pan: string;
  txn_number: string | null;
  logo: "ECARD" | string | null;
  type: string | null; // only 'Purchase' loaded
  transaction_amount: number | null;
  purchase: number | null;
  cash: number | null;
  status: string | null;
  eftpos_valid: boolean | null;
  taxicaller_job_id: string | null;

  // NZ-local projections from SQL (used for filtering/sort/display)
  txn_local_ts: string | null;    // e.g. "2025-09-29 07:17:16"
  txn_local_date: string | null;  // "YYYY-MM-DD" (NZ)
  txn_local_time: string | null;  // "HH:MM" (NZ)
};

/* ---------------- Component ---------------- */
const MatchHub: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
  // only sees their own linked driver(s)/vehicle(s) here -- e.g. a lease
  // operator's bookkeeper linked to one driver and one vehicle sees only
  // that driver's RideWise # and that vehicle's EFTPOS terminal, nothing
  // else. Unrestricted (the default -- everyone today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const readOnly = !canEditTab(effectivePermissions, "matchhub");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;
  const scopedVehicleNumbers = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedVehicleNumbers;

  // Week
  const [anchorDate, setAnchorDate] = useState(() => startOfWeekMon(new Date()));
  const weekStart = useMemo(() => startOfWeekMon(anchorDate), [anchorDate]);
  const weekEnd = useMemo(() => endOfWeekMonToSun(anchorDate), [anchorDate]);

  // Day pagination
  const [dayOffset, setDayOffset] = useState(0);
  const currentDayDate = useMemo(() => addDays(weekStart, dayOffset), [weekStart, dayOffset]);
  const currentDayIso = useMemo(() => fmtIsoLocal(currentDayDate), [currentDayDate]);

  // Filters
  const [driverOptions, setDriverOptions] = useState<string[]>([]);
  const [selDriver, setSelDriver] = useState("");
  const [rwDriverOptions, setRwDriverOptions] = useState<string[]>([]);
  const [selRwDriver, setSelRwDriver] = useState<string>("");
  const [eftposTids, setEftposTids] = useState<string[]>([]);
  const [eftposFilter, setEftposFilter] = useState<string>("");
  // ridewise_id (RW Driver #) for the selected DCP driver — used for Meta fallback
  const [selDriverRwId, setSelDriverRwId] = useState<string>("");

  // Data
  const [dcpRows, setDcpRows] = useState<DcpRow[]>([]);
  const [metrics, setMetrics] = useState<MetricRow[]>([
    { label: "EFTPOS", validated: 0, linked: 0, nulls: 0 },
    { label: "eCard", validated: 0, linked: 0, nulls: 0 },
    { label: "TM", validated: 0, linked: 0, nulls: 0 },
    { label: "TMHoist", validated: 0, linked: 0, nulls: 0 },
  ]);
  const [rwRows, setRwRows] = useState<RwRow[]>([]);
  const [eftRows, setEftRows] = useState<EftRow[]>([]);

  // UI
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [rwLoading, setRwLoading] = useState(false);
  const [eftLoading, setEftLoading] = useState(false);

  // DCP drafts
  const [drafts, setDrafts] = useState<Record<string, Partial<DcpRow>>>({});
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const dirtyRefs = useRef<{ [id: string]: HTMLTableRowElement | null }>({});

  // RW drafts
  const [rwDrafts, setRwDrafts] = useState<Record<number, Partial<RwRow>>>({});
  const rwDirtyRefs = useRef<{ [id: number]: HTMLTableRowElement | null }>({});

  // EFT drafts (+extra flag for DCP eCard valid)
  type EftDraftExtras = { ecard_valid_dcp?: boolean | null };
  const [eftDrafts, setEftDrafts] =
    useState<Record<number, Partial<EftRow> & EftDraftExtras>>({});
  const eftDirtyRefs = useRef<{ [id: number]: HTMLTableRowElement | null }>({});

  // ---------- Account Lookup Modal state ----------
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDcpJob, setEditDcpJob] = useState<{
    dcpId: string | number;
    taxicallerJobId: string;
    currentAccountId: string;
    currentPassenger: string;
    currentPickup: string;
    puDate: string;
    puTime: string;
  } | null>(null);

  const handleOpenEditModal = (row: DcpRow) => {
    setEditDcpJob({
      dcpId: row.id,
      taxicallerJobId: row.taxicaller_job_id,
      currentAccountId: String(row.Account_ID || ""),
      currentPassenger: row.passenger || "",
      currentPickup: firstBeforeComma(row.pickup),
      puDate: formatDate((row.pu_date || "").slice(0, 10)),
      puTime: fmtHHMM(row.pu_time),
    });
    setEditModalOpen(true);
  };

  const handleAccountApplied = (jobId: string, accountId: string, defaults: { project: string | null; costCode: string | null; reference: string | null }) => {
    // Update the local DCP row with the new account ID
    setDcpRows((prev) =>
      prev.map((r) =>
        (r.taxicaller_job_id || "") === jobId
          ? { ...r, Account_ID: accountId }
          : r
      )
    );
    toast({
      title: "Account Applied",
      description: `Job ${jobId} → A# ${accountId}. Billing fields backfilled.`,
    });
  };


  const getDraftRow = (row: DcpRow): DcpRow =>
    ({ ...row, ...(drafts[row.id] || {}) }) as DcpRow;
  const isRowDirty = (row: DcpRow) =>
    !!(drafts[row.id] && Object.keys(drafts[row.id]).length > 0);
  const updateDraftField = (rowId: string, field: keyof DcpRow, value: any) => {
    setDrafts((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [field]: value },
    }));
  };
  const clearRowDraft = (rowId: string) => {
    setDrafts((prev) => {
      const copy = { ...prev };
      delete copy[rowId];
      return copy;
    });
  };

  const getRwDraft = (row: RwRow): RwRow =>
    ({ ...row, ...(rwDrafts[row.id] || {}) }) as RwRow;
  const isRwDirty = (row: RwRow) =>
    !!(rwDrafts[row.id] && Object.keys(rwDrafts[row.id]).length > 0);
  const updateRwDraft = (rowId: number, field: keyof RwRow, value: any) => {
    setRwDrafts((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [field]: value },
    }));
  };
  const clearRwDraft = (rowId: number) => {
    setRwDrafts((prev) => {
      const copy = { ...prev };
      delete copy[rowId];
      return copy;
    });
  };

  const getEftDraft = (row: EftRow): EftRow & EftDraftExtras =>
    ({ ...row, ...(eftDrafts[row.id] || {}) });
  const isEftDirty = (row: EftRow) =>
    !!(eftDrafts[row.id] && Object.keys(eftDrafts[row.id]).length > 0);
  const updateEftDraft = (
    rowId: number,
    field: keyof (EftRow & EftDraftExtras),
    value: any
  ) => {
    setEftDrafts((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [field]: value },
    }));
  };
  const clearEftDraft = (rowId: number) => {
    setEftDrafts((prev) => {
      const copy = { ...prev };
      delete copy[rowId];
      return copy;
    });
  };

  /* ---------- Load drivers (DCP) - only active drivers with include_in_reports ---------- */
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select("driver_name, is_active, include_in_reports")
        .eq("is_active", true)
        .eq("include_in_reports", true)
        .order("driver_name");
      if (!error && data) {
        let names = data.map((d: any) => (d.driver_name || "").trim()).filter(Boolean);
        if (scopedDriverNames) {
          const scoped = new Set(scopedDriverNames);
          names = names.filter((n: string) => scoped.has(n));
        }
        setDriverOptions(names);
        // A restricted user linked to exactly one driver never needs to
        // pick -- select it for them straight away.
        if (scopedDriverNames && names.length === 1) setSelDriver(names[0]);
      }
    })();
  }, [scopedDriverNames]);


  /* ---------- Load RW driver_number list (only active drivers w/ ridewise_id) ---------- */
  useEffect(() => {
    (async () => {
      let query = supabase
        .from("drivers")
        .select("driver_name, ridewise_id, is_active, include_in_reports")
        .eq("is_active", true)
        .eq("include_in_reports", true)
        .not("ridewise_id", "is", null);
      if (scopedDriverNames) query = query.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
      const { data, error } = await query;
      if (error) {
        console.error(error);
        return;
      }
      const ids = Array.from(
        new Set(
          (data || [])
            .map((r: any) => String(r.ridewise_id || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => {
        const an = Number(a);
        const bn = Number(b);
        if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
        return a.localeCompare(b);
      });
      setRwDriverOptions(ids);
    })();
  }, [scopedDriverNames]);

  /* ---------- Load EFTPOS TIDs -- scoped to linked vehicle(s)' assigned
   *  terminal(s) for a restricted user, via vehicle_eftpos_machines (see
   *  FleetManagement.tsx for the same assignment table). ---------- */
  useEffect(() => {
    (async () => {
      if (scopedVehicleNumbers) {
        if (scopedVehicleNumbers.length === 0) {
          setEftposTids([]);
          return;
        }
        const { data, error } = await supabase
          .from("vehicle_eftpos_machines")
          .select("eftpos_machine_id, eftpos_machines(terminal_id)")
          .in("vehicle_number", scopedVehicleNumbers)
          .is("unassigned_at", null);
        if (error) {
          console.error(error);
          return;
        }
        const ids = Array.from(
          new Set((data || []).map((r: any) => r.eftpos_machines?.terminal_id).filter(Boolean))
        ).sort();
        setEftposTids(ids);
        return;
      }

      const { data, error } = await supabase.rpc("get_eftpos_tids");
      if (error) {
        console.error(error);
        return;
      }
      setEftposTids((data || []).map((r: any) => r.terminal_id).filter(Boolean));
    })();
  }, [scopedVehicleNumbers]);

  /* ---------- Matching results state ---------- */
  const [matchingResults, setMatchingResults] = useState<any>(null);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [lastRunType, setLastRunType] = useState<"backfill" | "full">("backfill");
  const [runCount, setRunCount] = useState(0);
  const [matchingStep, setMatchingStep] = useState<string>("");

  /* ---------- Helper: refresh all loaded data after matching ---------- */
  const refreshAllData = async () => {
    if (selDriver) loadDcpForDriver(selDriver);
    if (selRwDriver) loadRwForDriver(selRwDriver);
    if (eftposFilter) loadEftForTid(eftposFilter);
    loadMetrics();
  };

  /* ---------- Run Full Matching (sequential: Backfills → eCard Link → Combined EFTPOS/eCard → TM → TMH) ---------- */
  const runFullMatching = async () => {
    if (readOnly) return;
    try {
      setBusy(true);
      setLastRunType("full");
      setShowResultsDialog(true);
      const results: any = {};

      // Step 1: Backfills
      setMatchingStep("Step 1/5 — Running backfills…");
      const { data: bf, error: bfErr } = await supabase.rpc("fn_dcp_backfills");
      if (bfErr) throw new Error(`Backfill failed: ${bfErr.message}`);
      results.backfills = bf;

      // Step 2: eCard Link + Validate
      setMatchingStep("Step 2/5 — Running eCard link & validate…");
      const { data: ec, error: ecErr } = await supabase.rpc("fn_ecard_link_validate");
      if (ecErr) throw new Error(`eCard link failed: ${ecErr.message}`);
      results.ecard = Array.isArray(ec) ? ec[0] : ec;

      // Step 3: Combined EFTPOS + eCard Match
      setMatchingStep("Step 3/5 — Running combined EFTPOS/eCard match…");
      const { data: cm, error: cmErr } = await supabase.rpc("fn_combined_eftpos_ecard_match");
      if (cmErr) throw new Error(`Combined match failed: ${cmErr.message}`);
      const cmRow = Array.isArray(cm) ? cm[0] : cm;
      results.combined = cmRow;
      results.eftpos = {
        dcp_eftpos_rows_validated: cmRow?.dcp_eftpos_rows_validated ?? 0,
        eftpos_rows_linked_job_id: cmRow?.eftpos_rows_linked_job_id ?? 0,
        eftpos_rows_marked_valid: cmRow?.eftpos_rows_marked_valid ?? 0,
      };

      // Step 4: Total Mobility Match
      setMatchingStep("Step 4/5 — Running Total Mobility match…");
      const { data: tm, error: tmErr } = await supabase.rpc("fn_tm_link_validate");
      if (tmErr) throw new Error(`TM match failed: ${tmErr.message}`);
      results.tm = Array.isArray(tm) ? tm[0] : tm;

      // Step 5: TM Hoist Match
      setMatchingStep("Step 5/5 — Running TM Hoist match…");
      const { data: tmh, error: tmhErr } = await supabase.rpc("fn_tmh_link_validate");
      if (tmhErr) throw new Error(`TM Hoist match failed: ${tmhErr.message}`);
      results.tmh = Array.isArray(tmh) ? tmh[0] : tmh;

      setMatchingStep("Complete");
      setMatchingResults(results);
      setRunCount((c) => c + 1);
      toast({
        title: "Full Matching Complete",
        description: "All 5 steps ran successfully. Data refreshing…",
      });
      await refreshAllData();
    } catch (e: any) {
      console.error("Full matching error:", e);
      setMatchingStep("Failed");
      toast({ title: "Full Matching Failed", description: e.message });
    } finally {
      setBusy(false);
    }
  };


  /* ---------- Run Backfill Only ---------- */
  const runBackfillOnly = async () => {
    try {
      setBusy(true);
      setLastRunType("backfill");
      setShowResultsDialog(true);
      setMatchingStep("Running backfills…");
      const { data, error } = await supabase.rpc("fn_dcp_backfills");
      if (error) throw error;
      setMatchingStep("Complete");
      setMatchingResults({ backfills: data });
      setRunCount((c) => c + 1);
      toast({
        title: "Backfill Complete",
        description: "Missing data backfilled from TaxiCaller/Drivers/Vehicles. Data refreshing…",
      });
      await refreshAllData();
    } catch (e: any) {
      console.error("Backfill error:", e);
      setMatchingStep("Failed");
      toast({ title: "Backfill Failed", description: e.message });
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Legacy alias ---------- */
  const runMatching = runFullMatching;




  /* ---------- Metrics ---------- */
  const loadMetrics = async () => {
    setMetricsLoading(true);
    const s = fmtIsoLocal(weekStart);
    const e = fmtIsoLocal(weekEnd);
    try {
      const [eft, ec, tm, tmh] = await Promise.all([
        supabase
          .from("driver_completed_payments")
          .select("id", { count: "exact", head: true })
          .gte("pu_date", s)
          .lte("pu_date", e)
          .eq("eftpos_valid", true),
        supabase
          .from("driver_completed_payments")
          .select("id", { count: "exact", head: true })
          .gte("pu_date", s)
          .lte("pu_date", e)
          .eq("ecard_valid", true),
        supabase
          .from("driver_completed_payments")
          .select("id", { count: "exact", head: true })
          .gte("pu_date", s)
          .lte("pu_date", e)
          .eq("total_mobility_valid", true),
        supabase
          .from("driver_completed_payments")
          .select("id", { count: "exact", head: true })
          .gte("pu_date", s)
          .lte("pu_date", e)
          .eq("tm_hoist_valid", true),
      ]);
      setMetrics([
        { label: "EFTPOS", validated: eft.count || 0, linked: 0, nulls: 0 },
        { label: "eCard", validated: ec.count || 0, linked: 0, nulls: 0 },
        { label: "TM", validated: tm.count || 0, linked: 0, nulls: 0 },
        { label: "TMHoist", validated: tmh.count || 0, linked: 0, nulls: 0 },
      ]);
    } catch (e: any) {
      toast({ title: "Metrics error", description: e.message });
    } finally {
      setMetricsLoading(false);
    }
  };

  /* ---------- Load DCP rows (week, selected DCP driver) ---------- */
  const loadDcpForDriver = async (driver: string) => {
    if (!driver) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_dcp_for_driver_range", {
        p_driver_name: driver,
        p_start: fmtIsoLocal(weekStart),
        p_end: fmtIsoLocal(weekEnd),
      });
      if (error) throw error;
      setDcpRows(
        (data ?? []).map((r: any) => ({
          ...r,
          passenger: r.passenger || "-",
          pickup: r.pickup || "-",
        }))
      );
      setDrafts({});
    } catch (err: any) {
      toast({ title: "Load error", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selDriver) loadDcpForDriver(selDriver);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selDriver, weekStart.getTime(), weekEnd.getTime()]);

  /* ---------- Look up RW Driver # (ridewise_id) for the selected DCP driver ---------- */
  useEffect(() => {
    if (!selDriver) {
      setSelDriverRwId("");
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select("ridewise_id")
        .eq("driver_name", selDriver)
        .maybeSingle();
      if (!error && data) {
        setSelDriverRwId(String(data.ridewise_id || "").trim());
      } else {
        setSelDriverRwId("");
      }
    })();
  }, [selDriver]);

  /* ---------- Load RW rows (week, selected RW driver_number) ---------- */
  const loadRwForDriver = async (rwDriver: string) => {
    if (!rwDriver) {
      setRwRows([]);
      return;
    }
    setRwLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_rw_for_driver_range", {
        p_driver_number: rwDriver,
        p_start: fmtIsoLocal(weekStart),
        p_end: fmtIsoLocal(weekEnd),
      });
      if (error) throw error;
      setRwRows(data ?? []);
      setRwDrafts({});
    } catch (err: any) {
      toast({ title: "RideWise load error", description: err.message });
    } finally {
      setRwLoading(false);
    }
  };

  useEffect(() => {
    if (selRwDriver) loadRwForDriver(selRwDriver);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selRwDriver, weekStart.getTime(), weekEnd.getTime()]);

  /* ---------- Load EFT rows (week, selected TID) ---------- */
  const loadEftForTid = async (tid: string | null) => {
    if (!tid) {
      setEftRows([]);
      return;
    }
    setEftLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_eftpos_for_range", {
        p_start: fmtIsoLocal(weekStart),
        p_end: fmtIsoLocal(weekEnd),
        p_terminal_id: tid,
      });
      if (error) throw error;
      setEftRows(data ?? []);
      setEftDrafts({});
    } catch (err: any) {
      toast({ title: "EFTPOS load error", description: err.message });
    } finally {
      setEftLoading(false);
    }
  };

  useEffect(() => {
    if (eftposFilter) loadEftForTid(eftposFilter);
    else setEftRows([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eftposFilter, weekStart.getTime(), weekEnd.getTime()]);

  /* ---------- Summary tiles (based on loaded DCP rows) ---------- */
  const jobsCount = dcpRows.length;
  const paidTotal =
    sum(dcpRows.map((r) => r.cash_amt)) +
    sum(dcpRows.map((r) => r.eftpos_amt)) +
    sum(dcpRows.map((r) => r.totalmobility_amt)) +
    sum(dcpRows.map((r) => r.tm_hoist_amt)) +
    sum(dcpRows.map((r) => r.account_amt)) +
    sum(dcpRows.map((r) => r.other_amt)) +
    sum(dcpRows.map((r) => r.ecard_amt)) -
    sum(dcpRows.map((r) => r.discount));
  const validated = {
    eft: dcpRows.filter((r) => !!r.eftpos_valid).length,
    ecard: dcpRows.filter((r) => !!r.ecard_valid).length,
    tm: dcpRows.filter((r) => !!r.total_mobility_valid).length,
    tmh: dcpRows.filter((r) => !!r.tm_hoist_valid).length,
  };
  const metaVehicles =
    Array.from(new Set(dcpRows.map((r) => r.vehicle_number).filter(Boolean))).join(", ") ||
    "-";
  const metaTIDs =
    Array.from(
      new Set(dcpRows.map((r) => last3(r.eft_id)).filter((t) => t && t !== "-"))
    ).join(", ") || "-";
  // RW Driver # — prefer loaded RideWise rows, then the selected RW filter,
  // then the ridewise_id looked up for the selected DCP driver.
  const metaRwDrivers =
    Array.from(
      new Set(rwRows.map((r) => String(r.driver_number || "").trim()).filter(Boolean))
    ).join(", ") ||
    (selRwDriver ? selRwDriver : (selDriverRwId ? selDriverRwId : "-"));

  /* ---------- Export CSV (unchanged) ---------- */
  const exportCsv = () => {
    if (!selDriver || dcpRows.length === 0) return;
    const header = [
      "Date",
      "Time",
      "Veh",
      "TID",
      "Passenger",
      "Pick Up",
      "Amount_NZD",
      "Job_ID",
      "AccountAmt",
      "Cash",
      "EFTPOS_Valid",
      "TM_Valid",
      "TMH_Valid",
      "ACC_Flag",
      "Other_Flag",
      "Disc_Flag",
      "eCard_Valid",
      "Reconciled",
    ];
    const rows = dcpRows.map((r) => {
      const total =
        (r.cash_amt || 0) +
        (r.eftpos_amt || 0) +
        (r.totalmobility_amt || 0) +
        (r.tm_hoist_amt || 0) +
        (r.account_amt || 0) +
        (r.other_amt || 0) +
        (r.ecard_amt || 0) -
        (r.discount || 0);
      return [
        formatDate((r.pu_date || "").slice(0, 10)),
        fmtHHMM(r.pu_time),
        r.vehicle_number || "",
        last3(r.eft_id) || "",
        r.passenger || "",
        firstBeforeComma(r.pickup || ""),
        total.toFixed(2),
        r.taxicaller_job_id,
        r.account_amt > 0 ? r.account_amt.toFixed(2) : "0.00",
        r.cash_amt > 0 ? "1" : "0",
        r.eftpos_valid ? "1" : "0",
        r.total_mobility_valid ? "1" : "0",
        r.tm_hoist_valid ? "1" : "0",
        r.account_amt > 0 ? "1" : "0",
        r.other_amt > 0 ? "1" : "0",
        r.discount > 0 ? "1" : "0",
        r.ecard_valid ? "1" : "0",
        r.reconciled ? "1" : "0",
      ];
    });
    const csv = [header, ...rows]
      .map((arr) =>
        arr.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    // Sanitized for use in a filename: formatDate() can include "/" (DD/MM/YYYY or
    // MM/DD/YYYY tenants), which is unsafe inside a downloaded filename.
    const stamp = `${formatDate(fmtIsoLocal(weekStart))}-${formatDate(
      fmtIsoLocal(weekEnd)
    )}`.replace(/\s+/g, "").replace(/\//g, "-");
    a.href = url;
    a.download = `DCP_${selDriver}_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- Dirty badge counter (ALL row types) ---------- */
  const dcpDirtyCount = useMemo(
    () => dcpRows.filter((r) => isRowDirty(r)).length,
    [dcpRows, drafts]
  );
  const rwDirtyCount = useMemo(
    () => rwRows.filter((r) => isRwDirty(r)).length,
    [rwRows, rwDrafts]
  );
  const eftDirtyCount = useMemo(
    () => eftRows.filter((r) => isEftDirty(r)).length,
    [eftRows, eftDrafts]
  );
  const dirtyCount = dcpDirtyCount + rwDirtyCount + eftDirtyCount;

  const scrollToFirstDirty = () => {
    const first = dcpRows.find((r) => isRowDirty(r));
    if (first) {
      const el = dirtyRefs.current[first.id];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  /* ---------- Save handlers (RPC versions) ---------- */
  const saveRow = async (row: DcpRow) => {
    if (readOnly) return;
    const patch = drafts[row.id];
    if (!patch || Object.keys(patch).length === 0) return;

    try {
      setBusy(true);

      const { data, error } = await supabase.rpc("rpc_update_dcp_row", {
        p_id: row.id,
        p_cash: patch.cash_amt ?? null,
        p_eftpos: patch.eftpos_amt ?? null,
        p_tm: patch.totalmobility_amt ?? null,
        p_tmh: patch.tm_hoist_amt ?? null,
        p_acc: patch.account_amt ?? null,
        p_other: patch.other_amt ?? null,
        p_disc: patch.discount ?? null,
        p_ecard: patch.ecard_amt ?? null,
        p_eftpos_valid: typeof patch.eftpos_valid === "boolean" ? patch.eftpos_valid : null,
        p_tm_valid: typeof patch.total_mobility_valid === "boolean" ? patch.total_mobility_valid : null,
        p_tmh_valid: typeof patch.tm_hoist_valid === "boolean" ? patch.tm_hoist_valid : null,
        p_ecard_valid: typeof patch.ecard_valid === "boolean" ? patch.ecard_valid : null,
        p_reconciled: typeof patch.reconciled === "boolean" ? patch.reconciled : null,
      });

      if (error) throw error;
      if (!data) throw new Error("No row returned from rpc_update_dcp_row");

      setDcpRows((prev) =>
        prev.map((r) => (r.id === row.id ? ({ ...r, ...data } as DcpRow) : r))
      );
      clearRowDraft(row.id);
      toast({ title: "Saved", description: `Row ${row.taxicaller_job_id} updated.` });
    } catch (e: any) {
      console.error("DCP save error:", e);
      toast({ title: "Save failed", description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const saveRwRow = async (row: RwRow) => {
    if (readOnly) return;
    const patch = rwDrafts[row.id];
    if (!patch || Object.keys(patch).length === 0) return;

    const nextJob =
      typeof patch.taxicaller_job_id === "string" ? patch.taxicaller_job_id : row.taxicaller_job_id;
    const nextTmm =
      typeof patch.dcp_tmm_matched === "boolean" ? patch.dcp_tmm_matched : row.dcp_tmm_matched;
    const nextTmh =
      typeof patch.dcp_tmh_matched === "boolean" ? patch.dcp_tmh_matched : row.dcp_tmh_matched;

    try {
      setBusy(true);

      const { data, error } = await supabase.rpc("rpc_update_rw_row", {
        p_id: row.id,
        p_job_id: nextJob ?? null,
        p_tmm: nextTmm ?? null,
        p_tmh: nextTmh ?? null,
      });

      if (error) throw error;
      if (!data) throw new Error("No row returned from rpc_update_rw_row");

      setRwRows((prev) =>
        prev.map((r) => (r.id === row.id ? ({ ...r, ...data } as RwRow) : r))
      );
      clearRwDraft(row.id);
      toast({ title: "Saved", description: `RideWise row ${row.id} updated.` });
    } catch (e: any) {
      console.error("RideWise save error:", e);
      toast({ title: "Save failed", description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const saveEftRow = async (row: EftRow) => {
    if (readOnly) return;
    const patch = eftDrafts[row.id];
    if (!patch || Object.keys(patch).length === 0) return;

    const nextJob =
      (typeof patch.taxicaller_job_id === "string" ? patch.taxicaller_job_id : row.taxicaller_job_id) ?? null;
    const nextEftValid = typeof patch.eftpos_valid === "boolean" ? patch.eftpos_valid : null;
    const nextEcardValid =
      typeof (patch as any).ecard_valid_dcp === "boolean" ? (patch as any).ecard_valid_dcp : null;

    try {
      setBusy(true);

      const { data, error } = await supabase.rpc("rpc_update_eftpos_and_flags", {
        p_eft_id: row.id,
        p_job_id: nextJob,
        p_eft_valid: nextEftValid,
        p_ecard_valid: nextEcardValid,
      });

      if (error) throw error;
      if (!data) throw new Error("No row returned from rpc_update_eftpos_and_flags");

      // Update EFT in local state
      setEftRows((prev) =>
        prev.map((r) => (r.id === row.id ? ({ ...r, ...data } as EftRow) : r))
      );

      // Reflect DCP flags in UI if we have the DCP job in the list
      if (nextJob) {
        if (nextEftValid !== null) {
          setDcpRows((prev) =>
            prev.map((d) =>
              (d.taxicaller_job_id || "") === nextJob ? { ...d, eftpos_valid: nextEftValid } : d
            )
          );
        }
        if (nextEcardValid !== null) {
          setDcpRows((prev) =>
            prev.map((d) =>
              (d.taxicaller_job_id || "") === nextJob ? { ...d, ecard_valid: nextEcardValid } : d
            )
          );
        }
      }

      clearEftDraft(row.id);
      toast({ title: "Saved", description: `EFTPOS row ${row.id} updated.` });
    } catch (e: any) {
      console.error("EFTPOS save error:", e);
      toast({ title: "Save failed", description: e.message });
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Reconcile All (bulk reconcile all DCP rows for current day) ---------- */
  const reconcileAllDay = async () => {
    const unreconciled = dcpDayRows.filter((r) => {
      const merged = getDraftRow(r);
      return !merged.reconciled;
    });
    if (unreconciled.length === 0) {
      toast({ title: "Nothing to reconcile", description: "All jobs for this day are already reconciled." });
      return;
    }

    const confirmMsg = `Reconcile all ${unreconciled.length} unreconciled job${unreconciled.length !== 1 ? "s" : ""} for ${weekdayName(currentDayDate)} (${formatDate(currentDayIso)})?`;
    if (!window.confirm(confirmMsg)) return;

    setBusy(true);
    let saved = 0;
    let failed = 0;

    for (const row of unreconciled) {
      try {
        const { data, error } = await supabase.rpc("rpc_update_dcp_row", {
          p_id: row.id,
          p_cash: null,
          p_eftpos: null,
          p_tm: null,
          p_tmh: null,
          p_acc: null,
          p_other: null,
          p_disc: null,
          p_ecard: null,
          p_eftpos_valid: null,
          p_tm_valid: null,
          p_tmh_valid: null,
          p_ecard_valid: null,
          p_reconciled: true,
        });
        if (error) throw error;
        if (data) {
          setDcpRows((prev) =>
            prev.map((r) => (r.id === row.id ? ({ ...r, ...data } as DcpRow) : r))
          );
        }
        // Clear any pending draft for this row
        clearRowDraft(row.id);
        saved++;
      } catch (e: any) {
        console.error(`Reconcile failed for ${row.taxicaller_job_id}:`, e);
        failed++;
      }
    }

    setBusy(false);
    toast({
      title: "Reconcile All Complete",
      description: `${saved} job${saved !== 1 ? "s" : ""} reconciled${failed > 0 ? `, ${failed} failed` : ""}.`,
    });
  };


  /* ---------- Day scoping & interleaving ---------- */
  const dcpDayRows = useMemo(() => {
    const d = currentDayIso;
    return dcpRows
      .filter((r) => (r.pu_date || "").slice(0, 10) === d)
      .sort((a, b) => (a.pu_time || "").localeCompare(b.pu_time || ""));
  }, [dcpRows, currentDayIso]);

  const rwDayRows = useMemo(() => {
    const d = currentDayIso;
    const rows = rwRows.filter((r) => (r.trip_date || "").slice(0, 10) === d);
    return rows.sort((a, b) => {
      const at = (a.start_ts || "").slice(0, 19);
      const bt = (b.start_ts || "").slice(0, 19);
      if (at === bt) return (a.id || 0) - (b.id || 0);
      return at.localeCompare(bt);
    });
  }, [rwRows, currentDayIso]);

  // ✅ FIXED EFTPOS TIME (use proper NZ local timestamp)
  const eftDayRows = useMemo(() => {
    const d = currentDayIso;
    return eftRows
      .filter((r) => {
        const day = (r.txn_local_ts || r.transaction_date || "").slice(0, 10);
        return day === d;
      })
      .map((r) => {
        // Derive consistent HH:MM from txn_local_ts
        const ts = r.txn_local_ts ? new Date(r.txn_local_ts) : null;
        let hhmmss = "99:99";
        if (ts && !isNaN(ts.getTime())) {
          hhmmss = ts.toLocaleTimeString("en-NZ", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            timeZone: getAppTimeZone(),
          });
        }
        return { ...r, txn_local_time_fixed: hhmmss };
      })
      .sort((a, b) => a.txn_local_time_fixed.localeCompare(b.txn_local_time_fixed));
  }, [eftRows, currentDayIso]);

  const dcpJobOptions = useMemo(() => {
    return dcpRows
      .map((r) => (r.taxicaller_job_id || "").trim())
      .filter((v) => v.length > 0)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort();
  }, [dcpRows]);



  const rwByJob = useMemo(() => {
    const map = new Map<string, RwRow[]>();
    for (const r of rwDayRows) {
      const jid = (r.taxicaller_job_id || "").trim();
      if (!jid) continue;
      if (!map.has(jid)) map.set(jid, []);
      map.get(jid)!.push(r);
    }
    return map;
  }, [rwDayRows]);

  const rwUnmatched = useMemo(() => {
    return rwDayRows.filter((r) => !(r.taxicaller_job_id || "").trim());
  }, [rwDayRows]);

  // Group EFT rows by their linked job ID
  const eftByJob = useMemo(() => {
    const map = new Map<string, (EftRow & { txn_local_time_fixed?: string })[]>();
    for (const r of eftDayRows) {
      const jid = (r.taxicaller_job_id || "").trim();
      if (!jid) continue;
      if (!map.has(jid)) map.set(jid, []);
      map.get(jid)!.push(r);
    }
    return map;
  }, [eftDayRows]);

  // Only unmatched EFT rows go into the timeline
  const eftUnmatched = useMemo(() => {
    return eftDayRows.filter((r) => !(r.taxicaller_job_id || "").trim());
  }, [eftDayRows]);

  // *** FIXED: balanced and uses NZ-local EFT time
  const timeline = useMemo(() => {
    const toHms = (t?: string | null) => {
      if (!t) return "99:99:99";
      return t.length === 5 ? `${t}:00` : t.slice(0, 8);
    };

    type Event =
      | { kind: "DCP"; time: string; key: string; row: DcpRow }
      | { kind: "RWU"; time: string; key: string; row: RwRow }
      | { kind: "EFT"; time: string; key: string; row: EftRow };

    const events: Event[] = [];

    for (const r of dcpDayRows) {
      events.push({ kind: "DCP", time: toHms(r.pu_time), key: `D_${r.id}`, row: r });
    }

    for (const r of rwUnmatched) {
      events.push({ kind: "RWU", time: toHms(r.start_time), key: `R_${r.id}`, row: r });
    }

    // Only include unmatched EFT rows in the timeline
    for (const r of eftUnmatched) {
      const hhmmss = (r as any).txn_local_time_fixed || "99:99:99";
      events.push({ kind: "EFT", time: hhmmss, key: `E_${r.id}`, row: r });
    }

    events.sort((a, b) =>
      a.time === b.time ? a.key.localeCompare(b.key) : a.time.localeCompare(b.time)
    );
    return events;
  }, [dcpDayRows, rwUnmatched, eftUnmatched]);


  /* ---------------- Render ---------------- */
  /* ---------------- Render ---------------- */
  return (
    <div className="space-y-6">

      {dirtyCount > 0 && (
        <div
          onClick={scrollToFirstDirty}
          className="fixed top-4 right-6 z-50 bg-yellow-500 text-black px-4 py-2 rounded-full cursor-pointer shadow-lg animate-pulse hover:scale-105 transition-transform"
        >
          💾 {dirtyCount} unsaved {dirtyCount === 1 ? "change" : "changes"}
        </div>
      )}

      {/* Controls */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl text-primary">
            <CalendarDays className="w-5 h-5" /> Match Hub — Admin Validation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-sm mb-1">Week of</label>
              <Input
                type="date"
                value={fmtIsoLocal(weekStart)}
                onChange={(e) => {
                  const d = new Date(e.target.value + "T00:00:00");
                  setAnchorDate(startOfWeekMon(d));
                  setDayOffset(0);
                }}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => {
                const d = new Date(weekStart);
                d.setDate(d.getDate() - 7);
                setAnchorDate(d);
                setDayOffset(0);
              }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const d = new Date(weekStart);
                d.setDate(d.getDate() + 7);
                setAnchorDate(d);
                setDayOffset(0);
              }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <Button onClick={runFullMatching} disabled={busy}>
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Run Full Matching
            </Button>
            <Button variant="secondary" onClick={runBackfillOnly} disabled={busy}>
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Backfill Only
            </Button>

            <div className="ml-auto">
              <Badge variant="secondary">
                Week: {formatDate(fmtIsoLocal(weekStart))} – {formatDate(fmtIsoLocal(weekEnd))}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Filters */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl text-primary">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm mb-1">Driver (DCP)</label>
              <Select value={selDriver} onValueChange={(v) => setSelDriver(v)}>
                <SelectTrigger className="dark-select">
                  <SelectValue placeholder="Select driver_name" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {driverOptions.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm mb-1">RideWise Driver #</label>
              <Select value={selRwDriver} onValueChange={(v) => setSelRwDriver(v)}>
                <SelectTrigger className="dark-select">
                  <SelectValue placeholder="Select RW driver_number" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {rwDriverOptions.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm mb-1">EFTPOS Terminal ID</label>
              <Select value={eftposFilter} onValueChange={(v) => setEftposFilter(v)}>
                <SelectTrigger className="dark-select">
                  <SelectValue placeholder="Select TID (last 3 will show)" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {eftposTids.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t} ({last3(t)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm mb-1">Day</label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={dayOffset <= 0}
                  onClick={() => setDayOffset((x) => Math.max(0, x - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Badge variant="secondary" className="min-w-[140px] justify-center">
                  {weekdayName(currentDayDate, "short")}, {formatDate(currentDayDate)}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={dayOffset >= 6}
                  onClick={() => setDayOffset((x) => Math.min(6, x + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Driver section */}
      {(selDriver || selRwDriver || eftposFilter) && (
        <Card className="shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl text-primary">
              {selDriver ? `${selDriver}` : "(no DCP driver)"} — {formatDate(fmtIsoLocal(weekStart))} to {formatDate(fmtIsoLocal(weekEnd))}
            </CardTitle>
            <div className="text-sm text-muted-foreground">
              Showing <b>{weekdayName(currentDayDate)}</b> ({formatDate(currentDayIso)})
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Top tiles */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <Card className="bg-card/40">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">Jobs (Week, DCP)</div>
                  <div className="text-3xl font-bold">{jobsCount}</div>
                  <div className="mt-2 text-sm">
                    Reconciled: {dcpRows.filter((r) => r.reconciled).length}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/40">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">Validated (Week, DCP)</div>
                  <div className="space-y-1 text-sm mt-2">
                    <div>EFTPOS: <b>{validated.eft}</b></div>
                    <div>eCard: <b>{validated.ecard}</b></div>
                    <div>TM: <b>{validated.tm}</b></div>
                    <div>TMHoist: <b>{validated.tmh}</b></div>
                  </div>
                </CardContent>
              </Card>

<Card className="bg-card/40">
  <CardContent className="p-4">
    <div className="text-sm text-muted-foreground">Totals (NZD, Week, DCP)</div>
    <div className="text-2xl font-bold mb-2">Paid: ${fmtNum(paidTotal)}</div>

    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs leading-5">
      <div>Cash ${fmtNum(sum(dcpRows.map((r) => r.cash_amt)))}</div>
      <div>EFTPOS ${fmtNum(sum(dcpRows.map((r) => r.eftpos_amt)))}</div>

      <div>eCard ${fmtNum(sum(dcpRows.map((r) => r.ecard_amt)))}</div>
      <div>TM ${fmtNum(sum(dcpRows.map((r) => r.totalmobility_amt)))}</div>

      <div>TMH ${fmtNum(sum(dcpRows.map((r) => r.tm_hoist_amt)))}</div>
      <div>ACC ${fmtNum(sum(dcpRows.map((r) => r.account_amt)))}</div>

      <div>Other ${fmtNum(sum(dcpRows.map((r) => r.other_amt)))}</div>
      <div>Disc ${fmtNum(sum(dcpRows.map((r) => r.discount)))}</div>
    </div>
  </CardContent>
</Card>

              <Card className="bg-card/40">
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">Meta (Week, DCP)</div>
                  <div className="text-sm mt-2 space-y-0.5">
                    <div>
                      Vehicles: <span className="font-medium">{metaVehicles}</span>
                    </div>
                    <div>
                      TIDs: <span className="font-medium">{metaTIDs}</span>
                    </div>
                    <div>
                      RW Driver #: <span className="font-medium">{metaRwDrivers}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Scrollable table */}
           <div className="border rounded-md overflow-hidden">
  <div className="overflow-auto max-h-[68vh]">

    {/* ADD ONCE: shared list of Job IDs for type-ahead */}
    <datalist id="job-ids">
    {dcpJobOptions.map((jid) => (
    <option key={jid} value={jid} />
     ))}
    </datalist>

    <Table className="text-[11px]">
                  <TableHeader className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 shadow-sm">
                    <TableRow className="bg-orange-950/50">
                      {[
                        "Date",
                        "Time",
                        "Veh",
                        "TID",
                        "Passenger",
                        "Pick Up",
                        "$",
                        "Job #",
                        "A#",
                        "C",
                        "E",
                        "✓",
                        "TM",
                        "✓",
                        "TMH",
                        "✓",
                        "ACC",
                        "Other",
                        "DISC",
                        "eCard",
                        "✓",
                        "Reconciled",
                        "Save",
                      ].map((h) => (
                        <TableHead
                          key={h}
                          className={`text-center whitespace-nowrap py-1 px-1 ${
                            h === "Date" ? "min-w-[86px]" : ""
                          }`}
                        >
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {(loading || rwLoading || eftLoading) && (
                      <TableRow>
                        <TableCell colSpan={23} className="text-center py-3">
                          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                          Loading…
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading && !rwLoading && !eftLoading && timeline.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={23} className="text-center text-muted-foreground py-3">
                          No rows found for this day.
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading && !rwLoading && !eftLoading &&
                      timeline.map((evt) => {
                        if (evt.kind === "DCP") {
                          const r = evt.row as DcpRow;
                          const merged = getDraftRow(r);
                          const dirty = isRowDirty(r);
                          const active = activeRowId === r.id;

                          const total$ =
                            (merged.cash_amt || 0) +
                            (merged.eftpos_amt || 0) +
                            (merged.totalmobility_amt || 0) +
                            (merged.tm_hoist_amt || 0) +
                            (merged.account_amt || 0) +
                            (merged.other_amt || 0) +
                            (merged.ecard_amt || 0) -
                            (merged.discount || 0);

                          const rowClass = [
                            "transition-all",
                            active ? "ring-1 ring-orange-400/60" : "",
                            dirty ? "bg-yellow-900/20" : "bg-orange-900/10",
                          ].join(" ");

                          const matchedRw = (rwByJob.get(r.taxicaller_job_id) || []);

                          return (
                            <React.Fragment key={`DCP-${r.id}`}>
{/* DCP row */}
<TableRow
  ref={(el) => (dirtyRefs.current[r.id] = el)}
  id={`draft-${r.id}`}
  data-dirty={dirty ? "true" : "false"}
  className={rowClass}
  onFocus={() => setActiveRowId(r.id)}
  onBlur={(e) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setActiveRowId((id) => (id === r.id ? null : id));
    }
  }}
>
  <TableCell className="text-center py-0.5 px-0.5 min-w-[88px] font-mono">
    {formatDate((r.pu_date || "").slice(0, 10))}
  </TableCell>
  <TableCell className="text-center py-0.5 px-0.5">
    {fmtHHMM(r.pu_time)}
  </TableCell>
  <TableCell className="text-center py-0.5 px-0.5">
    {merged.vehicle_number || "-"}
  </TableCell>
  <TableCell className="text-center py-0.5 px-0.5">
    {merged.eft_id ? last3(merged.eft_id) : "-"}
  </TableCell>
  <TableCell className="text-center py-0.5 px-0.5">
  <div className="flex items-center justify-center gap-1">
    <span>{merged.passenger}</span>

    {merged.driver_notes && merged.driver_notes.trim() !== "" && (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border border-yellow-500/70 bg-yellow-500/10 hover:bg-yellow-500/20 p-0.5"
          >
            <MessageCircle className="w-3 h-3 text-yellow-300" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-pre-line text-xs leading-snug">
          {merged.driver_notes}
        </TooltipContent>
      </Tooltip>
    )}
  </div>
</TableCell>
  <TableCell className="text-center py-0.5 px-0.5">
    {firstBeforeComma(merged.pickup)}
  </TableCell>

  <TableCell className="text-right font-mono py-0.5 px-0.5">
    ${fmtNum(total$)}
  </TableCell>

  <TableCell className="text-center font-mono py-0.5 px-0.5">
    {r.taxicaller_job_id}
  </TableCell>
  <TableCell className="text-center py-0.5 px-0.5">
    <div className="flex items-center justify-center gap-0.5">
      <span className="truncate text-[11px]">{merged.Account_ID || "-"}</span>
      <button
        type="button"
        className="flex-shrink-0 p-0.5 rounded hover:bg-white/20 transition-colors"
        title="Edit account / CRM lookup"
        onClick={() => handleOpenEditModal(r)}
      >
        <Pencil className="w-3 h-3 opacity-60 hover:opacity-100" />
      </button>
    </div>
  </TableCell>


  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.cash_amt).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "cash_amt", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.eftpos_amt).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "eftpos_amt", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  {/* EFTPOS ✓/✗ pill — colored only if EFTPOS $ > 0 */}
  <TableCell className="text-center py-0.5 px-0.5">
    <BoolPillSelect
  value={merged.eftpos_valid}
  onChange={(next) => updateDraftField(r.id, "eftpos_valid", next)}
  enabled={(merged.eftpos_amt || 0) > 0}
/>
  </TableCell>

  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.totalmobility_amt).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "totalmobility_amt", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  {/* TM ✓/✗ pill — colored only if TM $ > 0 */}
  <TableCell className="text-center py-0.5 px-0.5">
    <BoolPillSelect
      value={merged.total_mobility_valid}
      onChange={(next) => updateDraftField(r.id, "total_mobility_valid", next)}
      enabled={(merged.totalmobility_amt || 0) > 0}
    />
  </TableCell>


  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.tm_hoist_amt).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "tm_hoist_amt", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  {/* TMH ✓/✗ pill — colored only if TMH $ > 0 */}
  <TableCell className="text-center py-0.5 px-0.5">
    <BoolPillSelect
      value={merged.tm_hoist_valid}
      onChange={(next) => updateDraftField(r.id, "tm_hoist_valid", next)}
      enabled={(merged.tm_hoist_amt || 0) > 0}
    />
  </TableCell>


  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.account_amt).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "account_amt", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.other_amt).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "other_amt", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.discount).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "discount", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  <TableCell className="text-right py-0.5 px-0.5">
    <Input
      type="number"
      step="0.01"
      value={Number(merged.ecard_amt).toFixed(2)}
      onFocus={() => setActiveRowId(r.id)}
      onChange={(e) =>
        updateDraftField(r.id, "ecard_amt", Number(e.target.value))
      }
      className="w-16 h-6 text-right text-[11px] bg-transparent border-border px-0.5"
    />
  </TableCell>

  {/* eCard ✓/✗ pill — colored only if eCard $ > 0 */}
  <TableCell className="text-center py-0.5 px-0.5">
    <BoolPillSelect
      value={merged.ecard_valid}
      onChange={(next) => updateDraftField(r.id, "ecard_valid", next)}
      enabled={(merged.ecard_amt || 0) > 0}
    />
  </TableCell>

  {/* Reconciled — always colored (green for ✓, red for ✗) */}
  <TableCell className="text-center py-0.5 px-0.5">
    <BoolPillSelect
      value={merged.reconciled}
      onChange={(next) => updateDraftField(r.id, "reconciled", next)}
      enabled={true}
      width="w-12"
    />
  </TableCell>


  <TableCell className="text-center py-0.5 px-0.5">
    <Button
      size="sm"
      disabled={!dirty || busy}
      onClick={() => saveRow(r)}
      className={`text-[10px] px-1.5 py-0.5 leading-none ${
        dirty
          ? "bg-yellow-500 text-black hover:bg-yellow-400"
          : "bg-gray-500 text-black opacity-50 cursor-not-allowed"
      }`}
    >
      {busy ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <span className="inline-flex items-center gap-1">
          <Save className="w-3 h-3" />
          Save
        </span>
      )}
    </Button>
  </TableCell>
</TableRow>

                              {/* Matched RideWise rows */}
                              {matchedRw.map((rw) => {
                                const m = getRwDraft(rw);
                                const dirtyRW = isRwDirty(rw);
                                const hoists = Math.max(0, m.hoist_true_count || 0);
                                const hoistMarks = "✓".repeat(hoists);
                                
                                // enable/disable color logic for unmatched RW rows
                                const tmmEnabled = (rw.concession_amount || 0) > 0;
                                const tmhEnabled = hoists > 0;

                                return (

                                  <TableRow
                                    key={`RW-${rw.id}`}
                                    ref={(el) => (rwDirtyRefs.current[rw.id] = el)}
                                    className={`transition-all bg-blue-900/25 text-yellow-100`}
                                  >
                                    <TableCell className="text-center py-0.5 px-0.5 min-w-[88px] font-mono">
                                      {formatDate((rw.trip_date || "").slice(0,10))}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {fmtHHMM(rw.start_time || "")}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {rw.vehicle_identifier || "-"}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      -
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {rw.client_first_name || "-"}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {firstBeforeComma(rw.pickup_location || "-")}
                                    </TableCell>

                                    <TableCell className="text-right font-mono py-0.5 px-0.5">
                                      ${fmtNum(rw.fare_total || 0)}
                                    </TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">
  <Input
    list="job-ids"
    placeholder="(unset)"
    value={(m.taxicaller_job_id ?? "")}
    onChange={(e) => {
      const v = e.target.value.trim();
      updateRwDraft(rw.id, "taxicaller_job_id", v.length ? v : null);
    }}
    className="h-6 text-[11px] px-1 w-[140px]"
  />
</TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                                    <TableCell className="text-right py-0.5 px-0.5">
                                      ${fmtNum(rw.concession_amount || 0)}
                                    </TableCell>

                                    {/* TM matched (coloured pill when there is a concession amount) */}
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      <BoolPillSelect
                                        value={!!m.dcp_tmm_matched}
                                        onChange={(next) => updateRwDraft(rw.id, "dcp_tmm_matched", next)}
                                        enabled={tmmEnabled}
                                      />
                                    </TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {hoists > 0 ? hoistMarks : "-"}
                                    </TableCell>

                                    {/* TMH matched (coloured pill when any hoist was recorded) */}
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      <BoolPillSelect
                                        value={!!m.dcp_tmh_matched}
                                        onChange={(next) => updateRwDraft(rw.id, "dcp_tmh_matched", next)}
                                        enabled={tmhEnabled}
                                      />
                                    </TableCell>


                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        disabled={!dirtyRW || busy}
                                        onClick={() => saveRwRow(rw)}
                                        className={`text-[10px] px-1.5 py-0.5 leading-none ${
                                          dirtyRW
                                            ? "bg-blue-400 text-black hover:bg-blue-300"
                                            : "bg-gray-500 text-black opacity-50 cursor-not-allowed"
                                        }`}
                                      >
                                        {busy ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <span className="inline-flex items-center gap-1">
                                            <Save className="w-3 h-3" />
                                            Save
                                          </span>
                                        )}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Matched EFTPOS rows (grouped under this DCP job) */}
                              {(eftByJob.get(r.taxicaller_job_id) || []).map((eft) => {
                                const m = getEftDraft(eft);
                                const dirtyE = isEftDirty(eft);

                                // Display helpers
                                const dateDisp = formatDate((eft.txn_local_date || "").slice(0, 10));
                                const timeDisp = (eft as any).txn_local_time_fixed || eft.txn_local_time || "-";
                                const isEcard = (eft.logo || "").toUpperCase() === "ECARD";

                                // Link to DCP job (needed for eCard valid)
                                const linkedJob = (m.taxicaller_job_id ?? eft.taxicaller_job_id) || "";
                                const dcpForThis = linkedJob
                                  ? dcpRows.find(d => (d.taxicaller_job_id || "") === linkedJob)
                                  : undefined;

                                const currentEcardValid = typeof m.ecard_valid_dcp === "boolean"
                                  ? m.ecard_valid_dcp
                                  : (dcpForThis?.ecard_valid ?? false);

                                // Enable rules (control red/green vs grey)
                                const eftEnabled = !isEcard && Number(eft.purchase || 0) > 0;
                                const ecardEnabled = isEcard && Number(eft.transaction_amount || 0) > 0 && !!linkedJob;

                                return (
                                  <TableRow
                                    key={`EFT-matched-${eft.id}`}
                                    ref={(el) => (eftDirtyRefs.current[eft.id] = el)}
                                    className="transition-all bg-emerald-900/30 text-yellow-100"
                                  >
                                    <TableCell className="text-center py-0.5 px-0.5 min-w-[88px] font-mono">
                                      {dateDisp}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {timeDisp}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {last3(eft.terminal_id)}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {eft.logo || "-"}
                                    </TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {eft.type || "-"}
                                    </TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">
                                      <div className="w-[110px] mx-auto">
                                        <Input
                                          list="job-ids"
                                          placeholder="(unset)"
                                          value={(m.taxicaller_job_id ?? eft.taxicaller_job_id) || ""}
                                          onChange={(ev) => {
                                            const v = ev.target.value.trim();
                                            updateEftDraft(eft.id, "taxicaller_job_id", v.length ? v : null);
                                          }}
                                          onBlur={(ev) => {
                                            const v = ev.target.value.trim();
                                            updateEftDraft(eft.id, "taxicaller_job_id", v.length ? v : null);
                                          }}
                                          inputMode="numeric"
                                          pattern="\d*"
                                          autoComplete="off"
                                          onKeyDown={(ev) => ev.stopPropagation()}
                                          className="h-6 w-full text-[11px] text-center px-1 font-mono tracking-tight bg-black/40"
                                        />
                                      </div>
                                    </TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">
                                      {last4(eft.pan)}
                                    </TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                                    {/* EFTPOS amount (non-eCard) */}
                                    <TableCell className="text-right py-0.5 px-0.5">
                                      {!isEcard ? `$${fmtNum(eft.purchase || 0)}` : "-"}
                                    </TableCell>

                                    {/* EFTPOS ✓/✗ pill — colored only if EFTPOS $ > 0 */}
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      <BoolPillSelect
                                        value={!!(m.eftpos_valid ?? eft.eftpos_valid)}
                                        onChange={(next) => updateEftDraft(eft.id, "eftpos_valid", next)}
                                        enabled={eftEnabled}
                                      />
                                    </TableCell>

                                    {/* unused cols */}
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                                    {/* eCard amount (only for eCard rows) */}
                                    <TableCell className="text-right py-0.5 px-0.5">
                                      {isEcard ? `$${fmtNum(eft.transaction_amount || 0)}` : "-"}
                                    </TableCell>

                                    {/* eCard ✓/✗ pill — colored only if eCard $ > 0 and linked to a DCP job */}
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      <BoolPillSelect
                                        value={!!currentEcardValid}
                                        onChange={(next) => updateEftDraft(eft.id, "ecard_valid_dcp", next)}
                                        enabled={ecardEnabled && !!linkedJob}
                                      />
                                    </TableCell>

                                    <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                                    {/* Save */}
                                    <TableCell className="text-center py-0.5 px-0.5">
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        disabled={!dirtyE || busy}
                                        onClick={() => saveEftRow(eft)}
                                        className={`text-[10px] px-1.5 py-0.5 leading-none ${
                                          dirtyE
                                            ? "bg-emerald-400 text-black hover:bg-emerald-300"
                                            : "bg-gray-500 text-black opacity-50 cursor-not-allowed"
                                        }`}
                                      >
                                        {busy ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <span className="inline-flex items-center gap-1">
                                            <Save className="w-3 h-3" />
                                            Save
                                          </span>
                                        )}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </React.Fragment>
                          );



} else if (evt.kind === "EFT") {
  const e = (evt as any).row as EftRow;
  const m = getEftDraft(e);
  const dirtyE = isEftDirty(e);

  // Display helpers
  const dateDisp = formatDate((e.txn_local_date || "").slice(0, 10));
  const timeDisp = (e as any).txn_local_time_fixed || e.txn_local_time || "-";
  const isEcard = (e.logo || "").toUpperCase() === "ECARD";

  // Link to DCP job (needed for eCard valid)
  const linkedJob = (m.taxicaller_job_id ?? e.taxicaller_job_id) || "";
  const dcpForThis = linkedJob
    ? dcpRows.find(d => (d.taxicaller_job_id || "") === linkedJob)
    : undefined;

  const currentEcardValid = typeof m.ecard_valid_dcp === "boolean"
    ? m.ecard_valid_dcp
    : (dcpForThis?.ecard_valid ?? false);

  // Enable rules (control red/green vs grey)
  const eftEnabled   = !isEcard && Number(e.purchase || 0) > 0;
  const ecardEnabled =  isEcard && Number(e.transaction_amount || 0) > 0 && !!linkedJob;

  return (
    <TableRow
      key={`EFT-${e.id}`}
      ref={(el) => (eftDirtyRefs.current[e.id] = el)}
      className="transition-all bg-emerald-900/30 text-yellow-100"
    >
      <TableCell className="text-center py-0.5 px-0.5 min-w-[88px] font-mono">
        {dateDisp}
      </TableCell>
      <TableCell className="text-center py-0.5 px-0.5">
        {timeDisp}
      </TableCell>
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
      <TableCell className="text-center py-0.5 px-0.5">
        {last3(e.terminal_id)}
      </TableCell>
      <TableCell className="text-center py-0.5 px-0.5">
        {e.logo || "-"}
      </TableCell>
      <TableCell className="text-center py-0.5 px-0.5">
        {e.type || "-"}
      </TableCell>

      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

<TableCell className="text-center py-0.5 px-0.5">
  <div className="w-[110px] mx-auto">
    <Input
      list="job-ids"
      placeholder="(unset)"
      value={(m.taxicaller_job_id ?? e.taxicaller_job_id) || ""}
      onChange={(ev) => {
        const v = ev.target.value.trim();
        updateEftDraft(e.id, "taxicaller_job_id", v.length ? v : null);
      }}
      onBlur={(ev) => {
        const v = ev.target.value.trim();
        updateEftDraft(e.id, "taxicaller_job_id", v.length ? v : null);
      }}
      inputMode="numeric"
      pattern="\d*"
      autoComplete="off"
      onKeyDown={(ev) => ev.stopPropagation()}  // prevent row keyboard traps
      className="h-6 w-full text-[11px] text-center px-1 font-mono tracking-tight bg-black/40"
    />
  </div>
</TableCell>

      <TableCell className="text-center py-0.5 px-0.5">
        {last4(e.pan)}
      </TableCell>

      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

      {/* EFTPOS amount (non-eCard) */}
      <TableCell className="text-right py-0.5 px-0.5">
        {!isEcard ? `$${fmtNum(e.purchase || 0)}` : "-"}
      </TableCell>

      {/* EFTPOS ✓/✗ pill — colored only if EFTPOS $ > 0 */}
      <TableCell className="text-center py-0.5 px-0.5">
        <BoolPillSelect
          value={!!(m.eftpos_valid ?? e.eftpos_valid)}
          onChange={(next) => updateEftDraft(e.id, "eftpos_valid", next)}
          enabled={eftEnabled}
        />
      </TableCell>

      {/* unused cols */}
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

      {/* eCard amount (only for eCard rows) */}
      <TableCell className="text-right py-0.5 px-0.5">
        {isEcard ? `$${fmtNum(e.transaction_amount || 0)}` : "-"}
      </TableCell>

      {/* eCard ✓/✗ pill — colored only if eCard $ > 0 and linked to a DCP job */}
      <TableCell className="text-center py-0.5 px-0.5">
        <BoolPillSelect
          value={!!currentEcardValid}
          onChange={(next) => updateEftDraft(e.id, "ecard_valid_dcp", next)}
          enabled={ecardEnabled && !!linkedJob}
        />
      </TableCell>


      <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

      {/* Save */}
      <TableCell className="text-center py-0.5 px-0.5">
        <Button
          size="sm"
          variant="secondary"
          disabled={!dirtyE || busy}
          onClick={() => saveEftRow(e)}
          className={`text-[10px] px-1.5 py-0.5 leading-none ${
            dirtyE
              ? "bg-emerald-400 text-black hover:bg-emerald-300"
              : "bg-gray-500 text-black opacity-50 cursor-not-allowed"
          }`}
        >
          {busy ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <span className="inline-flex items-center gap-1">
              <Save className="w-3 h-3" />
              Save
            </span>
          )}
        </Button>
      </TableCell>
    </TableRow>
  );
                        } else {
                          const rw = (evt as any).row as RwRow;
                          const m = getRwDraft(rw);
                          const dirtyRW = isRwDirty(rw);
                          const hoists = Math.max(0, m.hoist_true_count || 0);
                          const hoistMarks = "✓".repeat(hoists);

                          // enable/disable color logic for unmatched RW rows
                          const tmmEnabled = (rw.concession_amount || 0) > 0;
                          const tmhEnabled = hoists > 0;

                          return (

                            <TableRow
                              key={`RWU-${rw.id}`}
                              ref={(el) => (rwDirtyRefs.current[rw.id] = el)}
                              className={`transition-all bg-blue-900/25 text-yellow-100`}
                            >
                              <TableCell className="text-center py-0.5 px-0.5 min-w-[88px] font-mono">
                                {formatDate((rw.trip_date || "").slice(0,10))}
                              </TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">
                                {fmtHHMM(rw.start_time || "")}
                              </TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">
                                {rw.vehicle_identifier || "-"}
                              </TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">
                                {rw.client_first_name || "-"}
                              </TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">
                                {firstBeforeComma(rw.pickup_location || "-")}
                              </TableCell>

                              <TableCell className="text-right font-mono py-0.5 px-0.5">
                                ${fmtNum(rw.fare_total || 0)}
                              </TableCell>

                             {/* Link RW row to DCP Job — type or paste */}
<TableCell className="text-center py-0.5 px-0.5">
  <div className="w-[110px] mx-auto">
    <Input
      list="job-ids"
      placeholder="(unset)"
      value={m.taxicaller_job_id || ""}
      onChange={(ev) =>
        updateRwDraft(rw.id, "taxicaller_job_id", ev.target.value.trim() || null)
      }
      onBlur={(ev) =>
        updateRwDraft(rw.id, "taxicaller_job_id", ev.target.value.trim() || null)
      }
      inputMode="numeric"
      pattern="\d*"
      autoComplete="off"
      onKeyDown={(e) => e.stopPropagation()}
      /* keeps typing smooth in tables */
      className="h-6 w-full text-[11px] text-center px-1 font-mono tracking-tight bg-black/40"
    />
  </div>
</TableCell>

                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                              <TableCell className="text-right py-0.5 px-0.5">
                                ${fmtNum(rw.concession_amount || 0)}
                              </TableCell>

                              <TableCell className="text-center py-0.5 px-0.5">
                                <BoolPillSelect
                                  value={!!m.dcp_tmm_matched}
                                  onChange={(next) => updateRwDraft(rw.id, "dcp_tmm_matched", next)}
                                  enabled={tmmEnabled}
                                />
                              </TableCell>

                              <TableCell className="text-center py-0.5 px-0.5">
                                {hoists > 0 ? hoistMarks : "-"}
                              </TableCell>

                              <TableCell className="text-center py-0.5 px-0.5">
                                <BoolPillSelect
                                  value={!!m.dcp_tmh_matched}
                                  onChange={(next) => updateRwDraft(rw.id, "dcp_tmh_matched", next)}
                                  enabled={tmhEnabled}
                                />
                              </TableCell>


                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>
                              <TableCell className="text-center py-0.5 px-0.5">-</TableCell>

                              <TableCell className="text-center py-0.5 px-0.5">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={!dirtyRW || busy}
                                  onClick={() => saveRwRow(rw)}
                                  className={`text-[10px] px-1.5 py-0.5 leading-none ${
                                    dirtyRW
                                      ? "bg-blue-400 text-black hover:bg-blue-300"
                                      : "bg-gray-500 text-black opacity-50 cursor-not-allowed"
                                  }`}
                                >
                                  {busy ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <span className="inline-flex items-center gap-1">
                                      <Save className="w-3 h-3" />
                                      Save
                                    </span>
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      })}
                  </TableBody>
                </Table>

                {/* Sticky footer with Save All + Day nav */}
                <div className="sticky bottom-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-t">
                  <div className="flex items-center justify-between text-[12px] text-muted-foreground px-3 py-2">
                    <div>
                      Day: <b>{weekdayName(currentDayDate)}</b> ({formatDate(currentDayIso)})
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={dayOffset <= 0}
                        onClick={() => setDayOffset((x) => Math.max(0, x - 1))}
                      >
                        ‹ Prev Day
                      </Button>
                      <div>
                        {dayOffset + 1} / 7
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={dayOffset >= 6}
                        onClick={() => setDayOffset((x) => Math.min(6, x + 1))}
                      >
                        Next Day ›
                      </Button>
                      <Button
                        size="sm"
                        disabled={dcpDayRows.filter((r) => !r.reconciled).length === 0 || busy}
                        onClick={reconcileAllDay}
                        className={`text-[11px] px-3 py-1 leading-none font-semibold ${
                          dcpDayRows.filter((r) => !r.reconciled).length > 0
                            ? "bg-green-600 text-white hover:bg-green-500"
                            : "bg-gray-600 text-gray-300 opacity-50 cursor-not-allowed"
                        }`}
                        title="Reconcile all unreconciled DCP jobs for this day (Admin Tracking)"
                      >
                        {busy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : (
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        )}
                        Reconcile All{dcpDayRows.filter((r) => !r.reconciled).length > 0 ? ` (${dcpDayRows.filter((r) => !r.reconciled).length})` : ""}
                      </Button>

                      <Button
                        size="sm"
                        disabled={dirtyCount === 0 || busy}
                        onClick={async () => {
                          setBusy(true);
                          let saved = 0;
                          let failed = 0;
                          try {
                            // Save all dirty DCP rows
                            for (const row of dcpRows) {
                              if (isRowDirty(row)) {
                                try { await saveRow(row); saved++; } catch { failed++; }
                              }
                            }
                            // Save all dirty RW rows
                            for (const row of rwRows) {
                              if (isRwDirty(row)) {
                                try { await saveRwRow(row); saved++; } catch { failed++; }
                              }
                            }
                            // Save all dirty EFT rows
                            for (const row of eftRows) {
                              if (isEftDirty(row)) {
                                try { await saveEftRow(row); saved++; } catch { failed++; }
                              }
                            }
                            toast({
                              title: "Save All Complete",
                              description: `${saved} row${saved !== 1 ? "s" : ""} saved${failed > 0 ? `, ${failed} failed` : ""}.`,
                            });
                          } catch (err: any) {
                            toast({ title: "Save All Error", description: err.message });
                          } finally {
                            setBusy(false);
                          }
                        }}
                        className={`text-[11px] px-3 py-1 leading-none font-semibold ${
                          dirtyCount > 0
                            ? "bg-amber-500 text-black hover:bg-amber-400"
                            : "bg-gray-600 text-gray-300 opacity-50 cursor-not-allowed"
                        }`}
                        title="Save all unsaved changes before navigating"
                      >
                        {busy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : (
                          <Save className="w-3.5 h-3.5 mr-1" />
                        )}
                        Save All{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
                      </Button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
            {/* /Scrollable table */}
          </CardContent>
        </Card>
      )}

      {/* Export Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={exportCsv}>Export DCP CSV</Button>
      </div>

      {/* Matching / Backfill Results Dialog */}
      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {busy ? (
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
              ) : matchingStep === "Failed" ? (
                <AlertCircle className="w-5 h-5 text-red-500" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              )}
              {lastRunType === "full" ? "Full Matching Results" : "Backfill Results"}
            </DialogTitle>
          </DialogHeader>

          {/* Step progress indicator */}
          {busy && matchingStep && (
            <div className="flex items-center gap-3 p-3 rounded-md bg-blue-950/40 border border-blue-800/50">
              <Loader2 className="w-4 h-4 animate-spin text-blue-400 shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-blue-300">{matchingStep}</div>
                {lastRunType === "full" && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Pipeline: Backfills → eCard Link → EFTPOS/eCard → TM Match → TMH Match
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Failed indicator */}
          {!busy && matchingStep === "Failed" && (
            <div className="flex items-center gap-3 p-3 rounded-md bg-red-950/40 border border-red-800/50">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <div className="text-sm text-red-300">
                Operation failed. Check the console for details.
              </div>
            </div>
          )}

          {matchingResults && !busy && (
            <div className="space-y-4">
              {/* Backfills section */}
              {matchingResults.backfills && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Database className="w-4 h-4" /> Step 1: DCP Backfills
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {(Array.isArray(matchingResults.backfills)
                      ? matchingResults.backfills
                      : []
                    ).map((row: any, i: number) => (
                      <div key={i} className="bg-card/60 rounded p-2">
                        <span className="text-muted-foreground">{row.metric}:</span>{" "}
                        <span className={row.updated_rows > 0 ? "font-bold text-green-400" : "font-bold text-muted-foreground"}>
                          {row.updated_rows} updated
                        </span>
                        {" / "}
                        <span className={row.still_null_in_dcp > 0 ? "font-bold text-yellow-400" : "font-bold text-muted-foreground"}>
                          {row.still_null_in_dcp} still null
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}


              {/* eCard Link section (Step 2 - only in full matching) */}
              {matchingResults.ecard && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> Step 2: eCard Link &amp; Validate
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">DCP eCard Validated:</span>{" "}
                      <span className="font-bold text-green-400">{matchingResults.ecard.dcp_ecard_rows_validated ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">EFT Rows Linked:</span>{" "}
                      <span className="font-bold text-blue-400">{matchingResults.ecard.eft_rows_linked_job_id ?? 0}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Combined EFTPOS/eCard section (Step 3 - only in full matching) */}
              {matchingResults.combined && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Step 3: Combined EFTPOS/eCard Match
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">DCP eCard Validated:</span>{" "}
                      <span className="font-bold text-green-400">{matchingResults.combined.dcp_ecard_rows_validated ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">DCP EFTPOS Validated:</span>{" "}
                      <span className="font-bold text-green-400">{matchingResults.combined.dcp_eftpos_rows_validated ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">EFTPOS Rows Linked:</span>{" "}
                      <span className="font-bold text-blue-400">{matchingResults.combined.eftpos_rows_linked_job_id ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">EFTPOS Marked Valid:</span>{" "}
                      <span className="font-bold text-emerald-400">{matchingResults.combined.eftpos_rows_marked_valid ?? 0}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Total Mobility Match */}
              {matchingResults.tm && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> Step 4: Total Mobility Match
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">RW Rows Linked (Job ID):</span>{" "}
                      <span className="font-bold text-blue-400">{matchingResults.tm.primary_rows_set_with_job_id ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">Extra RW Matched:</span>{" "}
                      <span className="font-bold text-blue-400">{matchingResults.tm.extra_rows_marked_matched ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">DCP TM Validated:</span>{" "}
                      <span className="font-bold text-green-400">{matchingResults.tm.dcp_rows_validated ?? 0}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: TM Hoist Match */}
              {matchingResults.tmh && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> Step 5: TM Hoist Match
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">RW Rows w/ FK:</span>{" "}
                      <span className="font-bold text-blue-400">{matchingResults.tmh.primary_rows_with_fk ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">Extra RW Matched:</span>{" "}
                      <span className="font-bold text-blue-400">{matchingResults.tmh.extra_rows_marked_matched ?? 0}</span>
                    </div>
                    <div className="bg-card/60 rounded p-2">
                      <span className="text-muted-foreground">DCP Hoist Validated:</span>{" "}
                      <span className="font-bold text-green-400">{matchingResults.tmh.dcp_rows_hoist_validated ?? 0}</span>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          <div className="flex items-center justify-between mt-4 border-t pt-4">
            <div className="text-xs text-muted-foreground">
              {runCount > 0 && (
                <span>
                  Run #{runCount} — {lastRunType === "full" ? "Full Matching" : "Backfill Only"}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  if (lastRunType === "full") {
                    runFullMatching();
                  } else {
                    runBackfillOnly();
                  }
                }}
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCcw className="w-4 h-4 mr-2" />
                )}
                Run Again
              </Button>
              <Button variant="outline" onClick={() => setShowResultsDialog(false)}>
                Close
              </Button>
            </div>
          </div>

        </DialogContent>
      </Dialog>

      {/* Account Lookup Modal */}
      <AccountLookupModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        dcpJob={editDcpJob}
        onAccountApplied={handleAccountApplied}
      />

    </div>
  );
};


export default MatchHub;
